import React from 'react';
import {
  Box,
  Flex,
  Grid,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import {
  Printer,
  ShieldCheck,
  TrendUp,
  Receipt,
  UsersThree,
  Buildings,
  WarningCircle,
  Lightbulb,
  CurrencyDollar,
  IconProps,
} from 'phosphor-react';
import { formatCurrency } from '../../../../utils/FormatFunctions';
import { num } from '../format';
import { IDossie } from '../types';
import { UI } from '../ui';

interface DossieLaudoProps {
  emp: IDossie['empresa'];
  cliente?: IDossie['cliente'];
  interno?: IDossie['interno'];
  scoreNum?: number;
  scoreClassif?: string;
  periodoLabel: string;
  /** Responsável pela carteira (relacionamento_com_cliente), p/ assinatura do rodapé. */
  responsavel?: string;
}

const humanize = (k: string) => k.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());

const fmtValor = (k: string, v: unknown): string => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return typeof v === 'string' ? v : '—';
  if (k.includes('pct') || k.includes('margem') || k.includes('carga')) return `${n.toFixed(1)}%`;
  if (Math.abs(n) >= 1000) return formatCurrency(n);
  return n.toLocaleString('pt-BR');
};

const riscoColor = (status?: string): string => {
  if (status === 'red') return UI.neg;
  if (status === 'amber') return UI.warn;
  return UI.pos;
};

/** Ícone do card de destaque por heurística da chave. */
const iconeDe = (k: string): React.ComponentType<IconProps> => {
  if (k.includes('fatur') || k.includes('receita') || k.includes('venda')) return CurrencyDollar;
  if (k.includes('imposto') || k.includes('carga') || k.includes('tribut')) return Receipt;
  if (k.includes('folha') || k.includes('empreg') || k.includes('colab')) return UsersThree;
  if (k.includes('margem') || k.includes('result') || k.includes('lucro')) return TrendUp;
  return Buildings;
};

/** Selo numerado + título de seção (estilo laudo). */
function SecHeader({ n, title }: { n: string; title: string }) {
  return (
    <Flex align="center" gap={2.5} mb={3}>
      <Flex
        align="center"
        justify="center"
        minW="30px"
        h="24px"
        px={1.5}
        borderRadius="7px"
        bg={UI.infoSoft}
        color={UI.brand}
        fontSize="11px"
        fontWeight={800}
      >
        {n}
      </Flex>
      <Text fontSize="15px" fontWeight={800} color={UI.ink} letterSpacing="-0.2px">
        {title}
      </Text>
    </Flex>
  );
}

/** Gera recomendações determinísticas a partir dos apontamentos/riscos. */
function gerarRecomendacoes(interno?: IDossie['interno']): string[] {
  const recs: string[] = [];
  (interno?.apontamentos ?? [])
    .filter(a => String(a.status).toUpperCase().includes('DIVERG'))
    .slice(0, 5)
    .forEach(a => {
      const what = a.titulo || a.descricao || a.regra || 'apontamento';
      const dif = num(a.diferenca);
      const quando = a.periodo ? ` (${a.periodo})` : '';
      recs.push(
        dif
          ? `Revisar ${what}${quando}: divergência de ${formatCurrency(dif)} a conciliar.`
          : `Revisar ${what}${quando}: divergência apontada pelo motor de auditoria.`,
      );
    });
  (interno?.mapa_riscos ?? [])
    .filter(r => r.status === 'red' || r.status === 'amber')
    .slice(0, 3)
    .forEach(r => {
      if (r.label)
        recs.push(
          `Atenção em "${r.label}" — sinalizado como risco ${r.status === 'red' ? 'crítico' : 'moderado'}.`,
        );
    });
  if (!recs.length)
    recs.push('Nenhuma pendência crítica no período — manter o acompanhamento periódico.');
  return recs;
}

const GERADO_EM = (): string => {
  try {
    return new Date().toLocaleString('pt-BR');
  } catch {
    return '';
  }
};

export function DossieLaudo({
  emp,
  cliente,
  interno,
  scoreNum,
  scoreClassif,
  periodoLabel,
  responsavel,
}: DossieLaudoProps) {
  const nome = emp.razao_social || emp.nome || 'Empresa';
  const resumo = cliente?.resumo ?? {};
  const destaques = Object.entries(resumo).filter(([, v]) => v !== null && v !== undefined);
  const riscos = interno?.mapa_riscos ?? [];
  const apontamentos = interno?.apontamentos ?? [];
  const topClientes = cliente?.top_clientes ?? [];
  const socios = interno?.societario ?? [];
  const recomendacoes = gerarRecomendacoes(interno);

  const idLinha = [
    emp.cnpj && `CNPJ ${emp.cnpj}`,
    emp.regime && `Regime ${emp.regime}`,
    (emp.cidade || emp.uf) &&
      `${emp.cidade ?? ''}${emp.cidade && emp.uf ? '/' : ''}${emp.uf ?? ''}`,
    emp.external_id && `ID ${emp.external_id}`,
  ]
    .filter(Boolean)
    .join('  ·  ');

  return (
    <Box>
      {/* CSS de impressão: isola o laudo, esconde o resto do app */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #dossie-laudo, #dossie-laudo * { visibility: visible !important; }
          #dossie-laudo {
            position: absolute; left: 0; top: 0; width: 100%;
            box-shadow: none !important; border: none !important; border-radius: 0 !important;
          }
          .laudo-noprint { display: none !important; }
          @page { margin: 12mm; }
        }
      `}</style>

      {/* Toolbar (fora do #dossie-laudo → não sai no PDF) */}
      <Flex className="laudo-noprint" justify="flex-end" mb={4}>
        <Button
          size="sm"
          leftIcon={<Printer size={16} weight="bold" />}
          bg={UI.brand}
          color="white"
          _hover={{ bg: UI.brand, opacity: 0.92 }}
          onClick={() => window.print()}
        >
          Exportar PDF
        </Button>
      </Flex>

      {/* Documento */}
      <Box
        id="dossie-laudo"
        maxW="920px"
        mx="auto"
        bg="white"
        border="1px solid"
        borderColor={UI.border}
        borderRadius="16px"
        overflow="hidden"
        boxShadow="0 12px 40px -22px rgba(15,23,42,0.4)"
      >
        {/* Cabeçalho timbrado */}
        <Flex
          align="center"
          gap={4}
          px={7}
          py={5}
          bgGradient="linear(120deg, #0091D6, #1B88F5)"
          color="white"
        >
          <Flex
            align="center"
            justify="center"
            w="44px"
            h="44px"
            borderRadius="full"
            border="2px solid rgba(255,255,255,0.85)"
            flexShrink={0}
          >
            <ShieldCheck size={22} weight="fill" />
          </Flex>
          <Box flex={1} minW={0}>
            <Text fontSize="9px" fontWeight={800} letterSpacing="2px" opacity={0.85}>
              DOSSIÊ 360 · AUDITORIA INTELIGENTE
            </Text>
            <Text fontSize="22px" fontWeight={800} letterSpacing="-0.4px" noOfLines={1}>
              {nome}
            </Text>
            <Text fontSize="11.5px" opacity={0.92}>
              {idLinha}
            </Text>
          </Box>
          {scoreNum !== undefined && scoreNum !== null && (
            <Box textAlign="center" flexShrink={0}>
              <Text fontSize="34px" fontWeight={800} lineHeight="1" letterSpacing="-1.5px">
                {scoreNum}
                <Box as="span" fontSize="14px" opacity={0.7}>
                  /100
                </Box>
              </Text>
              <Text fontSize="10px" fontWeight={800} textTransform="uppercase" letterSpacing="1px">
                {scoreClassif ?? 'Score'}
              </Text>
            </Box>
          )}
        </Flex>

        <Flex
          px={7}
          py={3}
          bg={UI.infoSoft}
          justify="space-between"
          fontSize="11.5px"
          color={UI.muted}
        >
          <Text>
            Período de referência: <b>{periodoLabel}</b>
          </Text>
          <Text>Gerado em {GERADO_EM()}</Text>
        </Flex>

        <Box px={7} py={6}>
          {/* 01 — Destaques */}
          {destaques.length > 0 && (
            <Box mb={7}>
              <SecHeader n="01" title="Destaques do período" />
              <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={3}>
                {destaques.map(([k, v]) => {
                  const Icone = iconeDe(k);
                  return (
                    <Box
                      key={k}
                      bg="#F7FAFD"
                      border="1px solid"
                      borderColor={UI.border}
                      borderRadius="12px"
                      p={4}
                    >
                      <Flex
                        align="center"
                        justify="center"
                        w="34px"
                        h="34px"
                        borderRadius="9px"
                        bg={UI.infoSoft}
                        color={UI.brand}
                        mb={2}
                      >
                        <Icone size={18} weight="bold" />
                      </Flex>
                      <Text
                        fontSize="9.5px"
                        fontWeight={800}
                        color={UI.faint}
                        textTransform="uppercase"
                        letterSpacing="0.05em"
                      >
                        {humanize(k)}
                      </Text>
                      <Text
                        fontSize="17px"
                        fontWeight={800}
                        color={UI.ink}
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {fmtValor(k, v)}
                      </Text>
                    </Box>
                  );
                })}
              </Grid>
            </Box>
          )}

          {/* 02 — Mapa de riscos */}
          {riscos.length > 0 && (
            <Box mb={7}>
              <SecHeader n="02" title="Mapa de riscos" />
              <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={2.5}>
                {riscos.map(r => (
                  <Flex
                    key={r.label}
                    align="center"
                    gap={2.5}
                    p={3}
                    border="1px solid"
                    borderColor={UI.border}
                    borderRadius="10px"
                  >
                    <Box
                      w="10px"
                      h="10px"
                      borderRadius="full"
                      bg={riscoColor(String(r.status))}
                      flexShrink={0}
                    />
                    <Text fontSize="12.5px" color={UI.ink} flex={1}>
                      {r.label}
                    </Text>
                    <Text
                      fontSize="12.5px"
                      fontWeight={800}
                      color={UI.ink}
                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {typeof r.valor === 'object' || r.valor === null || r.valor === undefined
                        ? '—'
                        : fmtValor(String(r.label ?? ''), r.valor)}
                    </Text>
                  </Flex>
                ))}
              </Grid>
            </Box>
          )}

          {/* 03 — Recomendações */}
          <Box mb={7}>
            <SecHeader n="03" title="Recomendações" />
            <Box border="1px solid" borderColor={UI.border} borderRadius="12px" p={4} bg="#FCFDFE">
              {recomendacoes.map((rec, i) => (
                <Flex
                  key={rec}
                  gap={2.5}
                  mb={i === recomendacoes.length - 1 ? 0 : 2.5}
                  align="flex-start"
                >
                  <Box color={UI.warn} mt="2px" flexShrink={0}>
                    <Lightbulb size={15} weight="fill" />
                  </Box>
                  <Text fontSize="12.5px" color={UI.ink} lineHeight="1.55">
                    {rec}
                  </Text>
                </Flex>
              ))}
            </Box>
          </Box>

          {/* 04 — Principais clientes */}
          {topClientes.length > 0 && (
            <Box mb={7}>
              <SecHeader n="04" title="Principais clientes" />
              <TableContainer border="1px solid" borderColor={UI.border} borderRadius="12px">
                <Table size="sm" variant="simple">
                  <Thead bg={UI.rowHover}>
                    <Tr>
                      <Th>Cliente</Th>
                      <Th>UF</Th>
                      <Th isNumeric>Faturamento</Th>
                      <Th isNumeric>% do total</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {topClientes.map(c => (
                      <Tr key={`${c.nome}-${c.uf}`}>
                        <Td fontWeight={600} maxW="320px" whiteSpace="normal">
                          {c.nome}
                        </Td>
                        <Td>{c.uf ?? '—'}</Td>
                        <Td isNumeric sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatCurrency(num(c.total))}
                        </Td>
                        <Td isNumeric color={UI.muted}>
                          {num(c.pct_total).toFixed(1)}%
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* 05 — Apontamentos */}
          {apontamentos.length > 0 && (
            <Box mb={7}>
              <SecHeader n="05" title="Principais apontamentos" />
              <TableContainer border="1px solid" borderColor={UI.border} borderRadius="12px">
                <Table size="sm" variant="simple">
                  <Thead bg={UI.rowHover}>
                    <Tr>
                      <Th>Regra</Th>
                      <Th>Apontamento</Th>
                      <Th>Período</Th>
                      <Th isNumeric>Diferença</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {apontamentos.map(a => (
                      <Tr key={`${a.regra}-${a.periodo}-${a.titulo ?? a.descricao}`}>
                        <Td fontWeight={700}>{a.regra}</Td>
                        <Td maxW="320px" whiteSpace="normal">
                          {a.titulo || a.descricao}
                        </Td>
                        <Td>{a.periodo}</Td>
                        <Td
                          isNumeric
                          fontWeight={700}
                          color={num(a.diferenca) ? UI.neg : UI.muted}
                          sx={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {num(a.diferenca) ? formatCurrency(num(a.diferenca)) : '—'}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* 06 — Quadro societário */}
          {socios.length > 0 && (
            <Box mb={2}>
              <SecHeader n="06" title="Quadro societário" />
              <TableContainer border="1px solid" borderColor={UI.border} borderRadius="12px">
                <Table size="sm" variant="simple">
                  <Thead bg={UI.rowHover}>
                    <Tr>
                      <Th>Sócio</Th>
                      <Th>CPF/CNPJ</Th>
                      <Th>Cargo</Th>
                      <Th isNumeric>Participação</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {socios.map(s => (
                      <Tr key={`${s.cpf_cnpj}-${s.nome}`}>
                        <Td fontWeight={600} maxW="260px" whiteSpace="normal">
                          {s.nome}
                        </Td>
                        <Td>{s.cpf_cnpj}</Td>
                        <Td>{s.cargo}</Td>
                        <Td isNumeric sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {num(s.participacao).toFixed(1)}%
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>

        {/* Rodapé do laudo */}
        <Flex
          px={7}
          py={4}
          bg="#F7FAFD"
          borderTop="1px solid"
          borderColor={UI.border}
          justify="space-between"
          align="center"
          fontSize="10.5px"
          color={UI.muted}
          wrap="wrap"
          gap={2}
        >
          <Flex align="center" gap={1.5}>
            <WarningCircle size={13} weight="fill" />
            <Text>
              Documento confidencial — uso interno / cliente. Fonte: base Dominio + apurações.
            </Text>
          </Flex>
          <Text>{responsavel ? `Responsável: ${responsavel}` : 'Rede Especialistas'}</Text>
        </Flex>
      </Box>
    </Box>
  );
}
